import {UserPasswordUpdateApiInterface} from "../api/interface/user-password-update";
import {UserPasswordVerifyApiInterface} from "../api/interface/user-password-verify";
import {UserProfileApiInterface} from "../api/interface/user-profile";
import {UserProfileUpdateApiInterface} from "../api/interface/user-profile-update";
import type {
    UserPasswordUpdateRequest,
    UserProfileUpdateRequest,
    UserProfileUpdateResponse,
} from "../api/payload-types/user-profile";
import {setUserProfile} from "./accountManagementSlice";
import {selectCredentials, updateProfile} from "./authSlice";
import type {RevaThunkAction} from "./revaHooks";

/**
 * #19351: Loads the current user's account-management profile (full name/e-mail/language/editable).
 * Any failure (network, 503, forbidden) resolves to `null` — the same "not editable" state the UI shows
 * while nothing has loaded yet — instead of rejecting, so callers can simply fire-and-forget this.
 */
export const fetchUserProfileThunk =
    (): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}) => {
        const credentials = selectCredentials(getState());
        if (!credentials) {
            dispatch(setUserProfile(null));
            return;
        }
        try {
            const profile = await new UserProfileApiInterface().execute(serviceApi, credentials);
            dispatch(setUserProfile(profile));
        } catch {
            dispatch(setUserProfile(null));
        }
    };

/**
 * #19351: Saves full name / e-mail / language. Updates both the cached profile and the auth store's
 * `name`/`email`/`emailVerified` on success. Rejects with the raw `WebError` on 422/503 (see
 * {@link UserProfileUpdateApiInterface}) for the dialog to render itself.
 */
export const updateUserProfileThunk =
    (payload: UserProfileUpdateRequest): RevaThunkAction<Promise<UserProfileUpdateResponse>> =>
    async (dispatch, getState, {serviceApi}) => {
        const credentials = selectCredentials(getState())!;
        const response = await new UserProfileUpdateApiInterface(payload).execute(serviceApi, credentials);
        dispatch(setUserProfile(response.profile));
        dispatch(
            updateProfile({
                name: response.profile.fullName ?? undefined,
                email: response.profile.email,
                emailVerified: response.profile.emailVerified,
            }),
        );
        return response;
    };

/**
 * #19351: Changes the password. The stored JWT stays valid — no logout is triggered on success.
 * Rejects with the raw `WebError` on 422/503 (see {@link UserPasswordUpdateApiInterface}).
 */
export const updateUserPasswordThunk =
    (payload: UserPasswordUpdateRequest): RevaThunkAction<Promise<void>> =>
    async (_dispatch, getState, {serviceApi}) => {
        const credentials = selectCredentials(getState())!;
        await new UserPasswordUpdateApiInterface(payload).execute(serviceApi, credentials);
    };

/**
 * #19351: Checks the current password (step 1 of the profile dialog's password and e-mail change). Resolves when it
 * matches; rejects with the raw `WebError` on 422 (wrong password, too many attempts) or 503.
 */
export const verifyCurrentPasswordThunk =
    (currentPassword: string): RevaThunkAction<Promise<void>> =>
    async (_dispatch, getState, {serviceApi}) => {
        const credentials = selectCredentials(getState())!;
        await new UserPasswordVerifyApiInterface({currentPassword}).execute(serviceApi, credentials);
    };
